(() => {
  const storageKey = 'dhwWishlistV1';
  let hydratingProducts = false;

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch (error) {
      return;
    }
    sync();
    document.dispatchEvent(new CustomEvent('dhw:wishlist:change', { detail: { items } }));
  };

  const parseSizes = (value) => {
    if (!value) return [];
    try {
      const sizes = JSON.parse(value);
      return Array.isArray(sizes) ? sizes.map(String).filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  };

  const productFromButton = (button) => ({
    id: button.dataset.productId || '',
    handle: button.dataset.productHandle,
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || `/products/${button.dataset.productHandle}`,
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    vendor: button.dataset.productVendor || '',
    sizes: parseSizes(button.dataset.productSizes),
    sizesLoaded: true,
    selectedSize: '',
    selectedDate: ''
  });

  const sync = () => {
    const items = read();
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll('[data-dhw-wishlist-toggle]').forEach((button) => {
      const saved = handles.has(button.dataset.productHandle);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = button.querySelector('[data-dhw-wishlist-label]');
      if (label) label.textContent = saved ? 'Saved' : 'Add to wishlist';
      button.setAttribute('aria-label', `${saved ? 'Remove from' : 'Add to'} wishlist: ${button.dataset.productTitle || 'product'}`);
    });

    document.querySelectorAll('[data-dhw-wishlist-count]').forEach((count) => {
      count.dataset.count = String(items.length);
      count.textContent = '';
      count.hidden = items.length === 0;
    });
  };

  const toggle = (product) => {
    const items = read();
    const index = items.findIndex((item) => item.handle === product.handle);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(product);
    write(items);
  };

  const remove = (handle) => write(read().filter((item) => item.handle !== handle));

  const create = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const absoluteUrl = (url) => {
    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return url;
    }
  };

  const wishlistEmailBody = (items) => {
    const intro = "Here are the pieces I've saved to my wishlist:";
    const products = items.map((item, index) => {
      const selectedSize = item.selectedSize ? `\nPreferred size: ${item.selectedSize}` : '';
      const selectedDate = item.selectedDate ? `\nEvent date: ${item.selectedDate}` : '';
      const details = [item.title, item.price].filter(Boolean).join(' — ');
      return `${index + 1}. ${details}${selectedSize}${selectedDate}\n${absoluteUrl(item.url)}`;
    });
    return [intro, '', ...products].join('\n');
  };

  const extractProductSizes = (product) => {
    const options = Array.isArray(product.options) ? product.options : [];
    const sizeIndex = options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option && option.name;
      return String(name || '').trim().toLowerCase() === 'size';
    });
    if (sizeIndex < 0) return [];

    const option = options[sizeIndex];
    if (option && typeof option === 'object' && Array.isArray(option.values)) {
      return [...new Set(option.values.map(String).filter(Boolean))];
    }

    return [...new Set((product.variants || []).map((variant) => {
      if (Array.isArray(variant.options)) return variant.options[sizeIndex];
      return variant[`option${sizeIndex + 1}`];
    }).filter((value) => value != null && value !== '').map(String))];
  };

  const hydrateWishlistItems = async (items) => {
    if (hydratingProducts || !items.some((item) => !item.sizesLoaded)) return;

    hydratingProducts = true;
    const hydrated = await Promise.all(items.map(async (item) => {
      if (item.sizesLoaded) return item;
      try {
        const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        return { ...item, sizes: extractProductSizes(product), sizesLoaded: true };
      } catch (error) {
        return { ...item, sizes: [], sizesLoaded: true };
      }
    }));
    hydratingProducts = false;
    write(hydrated);
  };

  const renderWishlistPage = () => {
    const grid = document.querySelector('[data-dhw-wishlist-grid]');
    const empty = document.querySelector('[data-dhw-wishlist-empty]');
    const share = document.querySelector('[data-dhw-wishlist-share]');
    if (!grid || !empty) return;

    const items = read();
    grid.replaceChildren();
    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
    if (share) share.hidden = items.length === 0;

    items.forEach((item) => {
      const card = create('article', 'dhw-wishlist-card');
      const imageLink = create('a', 'dhw-wishlist-card__image-link');
      imageLink.href = item.url;
      if (item.image) {
        const image = create('img', 'dhw-wishlist-card__image');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.width = 900;
        image.height = 1200;
        imageLink.append(image);
      }

      const info = create('div', 'dhw-wishlist-card__info');
      if (item.vendor) info.append(create('p', 'dhw-wishlist-card__vendor', item.vendor));
      const title = create('a', 'dhw-wishlist-card__title', item.title);
      title.href = item.url;
      info.append(title);
      if (item.price) info.append(create('p', 'dhw-wishlist-card__price', item.price));

       const sizeField = create('label', 'dhw-wishlist-card__size-field');
      sizeField.append(create('span', 'dhw-wishlist-card__size-label', 'Preferred size'));
      const sizeSelect = create('select', 'dhw-wishlist-card__size-select');
      sizeSelect.dataset.dhwWishlistSize = item.handle;
      const sizePlaceholder = create('option', '', item.sizesLoaded ? 'Select size' : 'Loading sizes...');
      sizePlaceholder.value = '';
      sizeSelect.append(sizePlaceholder);
      (Array.isArray(item.sizes) ? item.sizes : []).forEach((size) => {
        const option = create('option', '', String(size));
        option.value = String(size);
        sizeSelect.append(option);
      });
      sizeSelect.value = item.selectedSize || '';
      sizeSelect.disabled = !item.sizesLoaded || !(item.sizes || []).length;
      sizeField.append(sizeSelect);
       info.append(sizeField);

       const dateField = create('label', 'dhw-wishlist-card__date-field');
       dateField.append(create('span', 'dhw-wishlist-card__date-label', grid.dataset.dateLabel || 'Event date'));
       const dateInput = create('input', 'dhw-wishlist-card__date-input');
       dateInput.type = 'date';
       dateInput.value = item.selectedDate || '';
       dateInput.title = grid.dataset.datePlaceholder || '';
       dateInput.dataset.dhwWishlistDate = item.handle;
       dateField.append(dateInput);
       info.append(dateField);

      const removeButton = create('button', 'dhw-wishlist-card__remove', 'Remove');
      removeButton.type = 'button';
      removeButton.dataset.dhwWishlistRemove = item.handle;
      removeButton.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
      card.append(imageLink, info, removeButton);
      grid.append(card);
    });

    hydrateWishlistItems(items);
  };

  document.addEventListener('click', (event) => {
    const toggleButton = event.target.closest('[data-dhw-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      toggle(productFromButton(toggleButton));
      return;
    }

    const removeButton = event.target.closest('[data-dhw-wishlist-remove]');
    if (removeButton) remove(removeButton.dataset.dhwWishlistRemove);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-dhw-wishlist-share-form]');
    if (!form) return;
    event.preventDefault();

    const emailInput = form.querySelector('[data-dhw-wishlist-share-email]');
    const share = form.closest('[data-dhw-wishlist-share]');
    const status = share && share.querySelector('[data-dhw-wishlist-share-status]');
    const subjectElement = share && share.querySelector('[data-dhw-wishlist-share-subject]');
    const items = read();

    if (!emailInput || !emailInput.reportValidity()) return;
    if (!items.length) {
      if (status) status.textContent = 'Add a piece to your wishlist before sharing it.';
      return;
    }

    const subject = subjectElement ? subjectElement.textContent.trim() : 'My wishlist';
    const mailto = `mailto:${encodeURIComponent(emailInput.value.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(wishlistEmailBody(items))}`;
    if (status) status.textContent = 'Your email app is opening with your wishlist ready to send.';
    window.location.href = mailto;
  });

  document.addEventListener('change', (event) => {
    const sizeSelect = event.target.closest('[data-dhw-wishlist-size]');
    const dateInput = event.target.closest('[data-dhw-wishlist-date]');
    if (!sizeSelect && !dateInput) return;
    const items = read();
    const handle = sizeSelect?.dataset.dhwWishlistSize || dateInput?.dataset.dhwWishlistDate;
    const item = items.find((entry) => entry.handle === handle);
    if (!item) return;
    if (sizeSelect) item.selectedSize = sizeSelect.value;
    if (dateInput) item.selectedDate = dateInput.value;
    write(items);
  });

  document.addEventListener('dhw:wishlist:change', renderWishlistPage);
  document.addEventListener('shopify:section:load', () => {
    sync();
    renderWishlistPage();
  });
  window.addEventListener('storage', () => {
    sync();
    renderWishlistPage();
  });

  window.DhwWishlist = { get: read, remove, toggle };
  sync();
  renderWishlistPage();
})();
